import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { isTruthy, openErrorNotification } from "helpers/methods";
import { RegisterUser, User } from "models/interfaces";
import history from "utils/history";
import {
  makeCall,
  getNoAuthCallParams,
  getAccessToken,
  getMFACallParams,
  getCallParamsWithoutCheckId,
  getCallParams,
  makeCallText,
} from "utils/service";
import { store } from "utils/store";

export async function login(email: string, password: string, token: string) {
  const body = {
    email: email,
    pwd: password,
    captchaToken: token,
    captchaVersion: "v3",
  };
  try {
    const callParams = getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.LOGIN, callParams);
    return response;
  } catch (error: any) {
    if (error.message === strings.pendingVerificationErrorMessage) {
      history.push({
        pathname: viewpaths.verificationPendingViewPath,
        state: { email: email, isFromLoginScreen: true },
      });
    } else {
      openErrorNotification(error.message);
    }
    throw error;
  }
}

export async function forgotPassword(email: string, token: string) {
  const body = {
    email: email,
    captchaToken: token,
    captchaVersion: "v3",
  };
  try {
    const callParams = await getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.RESET_PASSWORD, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function verifyTempPassword(token: string) {
  try {
    const callParams = await getNoAuthCallParams("POST", token);
    const response: any = await makeCall(urls.VERIFY_TEMP_PASSWORD, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function updatePwd(user: User, token: string) {
  try {
    const body = {
      ...user,
      captchaVersion: "v3",
    };
    const callParams = await getAccessToken("POST", body, token);
    const response: any = await makeCall(urls.UPDATE_PASSWORD, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function verifyTOTP(email: string, authenticatorCode: number) {
  try {
    const body = {
      email,
      authenticatorCode,
    };
    const callParams = getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.VERIFY_TOTP, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export const activateMFA = async (
  token: string,
  email: string,
  password: string,
  authenticatorCode: string
) => {
  try {
    const body = {
      email,
      pwd: password,
      authenticatorCode,
    };
    const callParams = await getMFACallParams(token, "POST", body);
    const response: any = await makeCall(urls.ACTIVATE_MFA, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const generateQRCodeForMFA = async (token: string) => {
  try {
    const callParams = await getMFACallParams(token, "GET");
    const response: any = await makeCall(urls.MFA_GET_QR_CODE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchPreAuthSecretKey = async (token: string) => {
  try {
    const callParams = await getMFACallParams(token, "GET");
    const response: any = await makeCallText(
      urls.MFA_GET_PREAUTH_KEY,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const saveFirebaseToken = async (
  accessToken: string,
  token: string,
  email: string
) => {
  try {
    const body = {
      userId: email,
      token,
    };
    const callParams = await getMFACallParams(accessToken, "POST", body);
    const response: any = await makeCall(urls.SAVE_FIREBASE_TOKEN, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const removeDeviceId = async () => {
  try {
    const deviceId = store.getState().auth.deviceId;
    const user = {
      deviceIds: isTruthy(deviceId) ? [deviceId] : [],
    };
    const callParams = getCallParamsWithoutCheckId("PATCH", user);
    const response: any = await makeCall(urls.REMOVE_DEVICE_ID, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const createNewAccount = async (user: RegisterUser) => {
  try {
    const body = {
      ...user,
      captchaVersion: "v3",
    };
    const callParams = getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.REGISTER_USER, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export async function resendAccountVerificationNotification(userEmail: string) {
  try {
    const callParams = await getNoAuthCallParams("POST", { email: userEmail });
    const response: any = await makeCall(
      urls.RESEND_ACCOUNT_VERIFICATION_EMAIL,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
}

export const getManager = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.IS_MANAGER, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getFirstNameMandatory = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(
      urls.IS_FIRST_NAME_MANDATORY,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchCampaignRetentionDuration = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(
      urls.campaignRetentionDuration,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};
